<!--
  - Copyright (c) 2022. Haodong JU
  -->

<template>
  <large-base-card>
    <div class="d-flex align-center">
      <div
        class="text-h3"
        style="height: 48px"
      >
        {{ $t('turnover') }}
      </div>
    </div>
    <div style="display: grid;grid-template-columns: minmax(0,0.6fr) minmax(0,1fr);grid-gap: 24px">
      <div class="d-flex align-center justify-center text-body-2">
        <div>
          <div
            v-if="!graphReady"
            class="d-flex align-center justify-center"
            style="height: 400px"
          >
            <v-progress-circular
              class="mt-n7"
              indeterminate
            />
          </div>
          <div
            v-else
            style="width: min-content"
          >
            <div
              class="text-h1 font-weight-bold text-no-wrap"
              style="width: fit-content"
            >
              {{ totalTurnover | priceDisplay }}
            </div>
            <div>
              {{ $t('todayTurnoverIncludeUnpaid') }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="!graphReady"
          class="d-flex align-center justify-center"
          style="height: 400px"
        >
          <v-progress-circular
            class="mt-n7"
            indeterminate
          />
        </div>
        <apexchart
          v-else
          :options="options"
          :series="series"
          type="bar"
          width="100%"
        />
      </div>
    </div>
  </large-base-card>
</template>
<script>


  import { loadDashboardData } from '@/model/DashboardService'
  import dayjs from 'dayjs'
  import { predefinedDateRangeList, today } from '@/common/Repository/DateRepository'
  import LargeBaseCard from '@/composable/LargeBaseCard'
  import { isNumber } from 'lodash-es'
  import i18n from '../../../i18n'

  export default {
    components: {
      LargeBaseCard,
    },
    props: {
      dataInfo: {},
      label: {
        type: String,
        default: '',
      },
      infoToday: {},
    },
    data: function () {
      return {
        info: {
          durationTimeData: [],
          spanTurnover: { result: [] },
        },
        graphReady: false,
        series: [],
        options: {},
        date: [today, today],
      }
    },
    computed: {
      totalTurnover () {
        return this.dataInfo?.taxInfos?.content?.total
      },
    },
    async mounted () {
      await this.initGraph()
    },
    methods: {

      getDataFromTurnoverData (categories, list, dayPad = 0) {
        return categories?.map(d => {
          return list.find(it => dayjs(it.smallSelector).add(dayPad, 'd').format('DD.MM') === d)?.spanTotal ?? 0
        })
      },
      async initGraph () {
        this.graphReady = false
        const [start, end] = predefinedDateRangeList[2].dateRange()
        const categories = []
        let tmp = dayjs(start)
        const endD = dayjs(end)
        while (tmp.isBefore(endD)) {
          categories.push(tmp.format('DD.MM'))
          tmp = tmp.add(1, 'd')
        }
        const recent7DaysInfo = (await loadDashboardData([start, end])).spanTurnover
        const last7DaysInfo = (await loadDashboardData(predefinedDateRangeList[3].dateRange())).spanTurnover

        const data = this.getDataFromTurnoverData(categories, recent7DaysInfo.result)
        const dataOld = this.getDataFromTurnoverData(categories, last7DaysInfo.result, 7)
        this.series = [
          {
            name: this.$i18n.t('past_period'),
            data: dataOld,
          },
          {
            name: this.$i18n.t('last_7_days'),
            data: data,
          },
        ]

        this.options = {
          noData: {
            text: i18n.t('no_data_jet'),
          },
          colors: ['#77B6EA', '#367aeb'],
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '10px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontWeight: 'bold',
              colors: ['#1B5E20'],
            },
            formatter: function (value, { seriesIndex, dataPointIndex, w }) {
              return value + '€'
            },
            offsetY: -8,
            distributed: true,
            background: {
              enabled: true,
              foreColor: '#fff',
              padding: 4,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: '#fff',
              opacity: 0.9,
              dropShadow: {
                enabled: false,
                top: 1,
                left: 1,
                blur: 1,
                color: '#000',
                opacity: 0.45,
              },
            },
          },
          grid: { strokeDashArray: 7 },
          chart: {
            id: 'sales-chart',
            height: 400,
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
            },
          },
          markers: {
            size: 2,
          },
          tooltip: {
            y: {
              formatter: (val) => {
                return (isNumber(val) ? val.toFixed(2) : 0) + ' €'
              },
            },
          },
          stroke: {
            curve: 'smooth',
          },

          yaxis: {
            forceNiceScale: true,
            min: 0,
            labels: {
              formatter: (val) => {
                if (val > 999) {
                  return (parseFloat(val) / 1000).toFixed(1) + 'k'
                } else if (val > 99) {
                  return parseFloat(val).toFixed(0)
                } else {
                  return parseFloat(val).toFixed(1)
                }
              },
            },
          },
          xaxis: {
            categories: categories,
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              distribute: true,
              columnWidth: '60%',
              dataLabels: {
                position: 'top',
                maxItems: 12,
                hideOverflowingLabels: true,
              },
            },

          },
        }

        this.graphReady = true
      },
    },
  }
</script>

<style lang="scss" scoped>
.chart-wrap {
  max-width: 560px;
  max-height: 280px;
}
</style>
